// What Talk says while the brain looks something up — T120 (26 Sep 2026), by stage since T120a.
//
// Natanel, 26 Sep 2026: "at the moment it only hears clicking sounds whilst the agent thinks and
// looks up, it should speak as well with fillers, like one moment, just getting that for you,
// nearly there, got it, hmmm, and other stuff that are human to fill the silence rather than just
// typing in the background. We are creating an AI Mona!" And, the same day, on hearing the plan:
// "there should still be typing and maybe some humming in the background or whatever like a normal
// person would do to fill the silence between fillers". So the typing stays (ElevenLabs' own sound,
// services/bff/src/elevenlabs.ts), her words go over it, and between them something quieter.
//
// T120a, the same evening, on hearing the first version: "saying 'nearly there' after the first two
// seconds is silly, the fillers should be progressive and indicative of the stage in the lookup
// they are in. and they should sound like a human, not an annoying AI". So every line belongs to a
// stage of the lookup, read off the find's own stream (apps/console/src/voice-fillers.ts): `start`
// before it has reached for anything, `look` once it has, `again` when it goes back for more,
// `long` when it has simply taken long, and `write` while the answer is being written, the only
// time "nearly there" is true. Fewer of them than before, and rendered soft, the way someone talks
// to themselves while they look ([softly], [thoughtful]: delivery cues her voice model reads).
//
// ElevenLabs cannot do the words on its side: its soft-timeout fillers cover a slow model, and a
// probe on 26 Sep 2026 (a throwaway agent, a 15 second ask_vera) heard nothing from them while a
// client tool was running. So the brain plays these itself, in her voice, while `ask_vera` runs.
// The clips are rendered once in her voice (`pnpm --filter @vera/bff voice:fillers`) and ship in
// the brain's bundle.
//
// Data only: the brain's bundle imports this file, so nothing here may reach for Node.

/** A stage of the lookup a line belongs to (T120a). */
export type FillerStage = 'start' | 'look' | 'again' | 'long' | 'write';

export interface VoiceFiller {
  /** The clip's file name, without `.mp3`. */
  id: string;
  /**
   * What she says, as the speech model is given it: short, the way Mona fills a pause, and no dash
   * (T114). A bracketed cue ("[softly]") is a delivery direction her voice model reads, never words.
   */
  text: string;
  /**
   * When she says it. `start`: the first line, while the lookup has reached for nothing yet. `look`:
   * once it has reached for the records. `again`: it went back for more. `long`: it has taken long,
   * with nothing new to say. `write`: its answer is being written. `murmur`: quieter
   * (MURMUR_VOLUME), in a gap with nothing new to say. `done`: once, as the answer arrives.
   */
  when: FillerStage | 'murmur' | 'done';
}

export const VOICE_FILLERS: readonly VoiceFiller[] = [
  { id: 'start-pull', text: '[softly] Okay, pulling it up...', when: 'start' },
  { id: 'start-sec', text: '[thoughtful] Mm, okay, one sec...', when: 'start' },
  { id: 'start-see', text: "[softly] Right, let's see...", when: 'start' },
  { id: 'look-going', text: '[thoughtful] Okay, going through them now...', when: 'look' },
  { id: 'look-reading', text: '[softly] Mm, just reading through...', when: 'look' },
  { id: 'look-few', text: '[thoughtful] Let me just check a few things...', when: 'look' },
  { id: 'again-one', text: '[softly] One more thing to check...', when: 'again' },
  { id: 'again-look', text: '[thoughtful] And one more look...', when: 'again' },
  { id: 'long-bear', text: "[softly] Bear with me, won't be long...", when: 'long' },
  { id: 'long-still', text: '[thoughtful] Still on it...', when: 'long' },
  { id: 'long-sorry', text: "[softly] Sorry, this one's taking a bit...", when: 'long' },
  { id: 'write-nearly', text: '[softly] Okay, nearly there...', when: 'write' },
  { id: 'write-together', text: '[thoughtful] Just putting it together...', when: 'write' },
  { id: 'hmm', text: 'Hmm...', when: 'murmur' },
  { id: 'mm-hmm', text: 'Mm-hmm.', when: 'murmur' },
  { id: 'hum-a', text: '[hums quietly] Mm, mm-mm, mmm.', when: 'murmur' },
  { id: 'hum-b', text: '[hums] Mmm mm mm, mmm.', when: 'murmur' },
  { id: 'hum-c', text: 'Mmm... mm-hmm... mmm.', when: 'murmur' },
  { id: 'done-got', text: 'Okay, got it.', when: 'done' },
  { id: 'done-here', text: 'Right, here we go.', when: 'done' },
];

/**
 * The first line, after her own "Let me check that for you" (the tool's forced pre-tool speech) and
 * a few seconds of typing. A lookup that answers sooner is never filled.
 */
export const FILLER_FIRST_MS = 5_000;
/**
 * After a clip ends, the next moment she may say something: a line when the lookup has moved on
 * (or taken long), otherwise perhaps a murmur, otherwise just the typing.
 */
export const FILLER_EVERY_MS = 6_000;
/** Up to this much either side of FILLER_EVERY_MS, so the rhythm is not a metronome's. */
export const FILLER_JITTER_MS = 1_000;
/** From the lookup's start, when a `long` line may be said with nothing new to say... */
export const FILLER_LONG_MS = 18_000;
/** ...and how long after one before the next. */
export const FILLER_LONG_EVERY_MS = 16_000;
/** How often a moment with nothing new to say gets a murmur; never two running, none while writing. */
export const MURMUR_CHANCE = 0.5;
/** A murmur is under her breath: this loud, where her words are at full volume. */
export const MURMUR_VOLUME = 0.5;
